import * as React from 'react';

import {AiSuggestionPill} from 'shells/ai-suggestion-pill/ai-suggestion-pill';
import {FormField} from 'shells/form/form-field';
import {TextField} from 'shells/textfield/textfield';
import {Body} from 'shells/typography/body';

import type {CampaignModalType} from '../../editions/new-edition-modal';

import styles from './nutshell-ai-empty-state.module.css';

type Props = {
    websiteUrl: string;
    campaignDescription: string;
    websiteUrlError: string | null | undefined;
    onWebsiteUrlChange: (websiteUrl: string) => void;
    onCampaignAboutChange: (campaignDescription: string) => void;
    campaignModalType: CampaignModalType;
};

export function NutshellAiEmptyState(props: Props) {
    return (
        <>
            <div className='mb-24'>
                <Body isBold={true} color='dark'>
                    Describe your campaign goal and let AI create a personalized{' '}
                    {getEditionTypeLabel(props.campaignModalType)}
                </Body>
            </div>
            <FormField
                innerId='url-input'
                label={
                    <Body size='small' isBold={true}>
                        Use design and content from
                    </Body>
                }
                tooltipInfo='We’ll inspect your website for colors, logos, images, and content for your broadcast. This will help us generate a more personalized and impactful broadcast.'
                errorMessage={props.websiteUrlError}
            >
                <TextField
                    placeholder='e.g. https://mybusiness.com/blog/announcing-new-product'
                    value={props.websiteUrl}
                    onChange={props.onWebsiteUrlChange}
                    focusOnMount={true}
                />
            </FormField>
            <FormField
                innerId='campaign-description-input'
                label={
                    <Body size='small' isBold={true}>
                        What is this campaign about?
                    </Body>
                }
                tooltipInfo='The more details you share, the more personalized and impactful your campaign will be.'
                noMargin={true}
            >
                <div className={styles['textfield-container']}>
                    <TextField
                        placeholder='E.g., Announce our winter sale with 30% off all products'
                        multiline={true}
                        value={props.campaignDescription}
                        onChange={props.onCampaignAboutChange}
                    />
                </div>
            </FormField>
            <div className='flex flex-dir-col align-center gap-12 mt-12'>
                <Body color='light' size='small'>
                    Or get started with these prompts
                </Body>
                <div className='flex gap-8'>
                    {getStarterPromptsConfig(props.campaignModalType).map(({label, prompt}) => (
                        <AiSuggestionPill
                            key={label}
                            label={label}
                            onClick={() => {
                                props.onCampaignAboutChange(prompt);
                            }}
                        />
                    ))}
                </div>
            </div>
        </>
    );
}

type StarterPrompt = {
    label: string;
    prompt: string;
};

const broadcastAndTemplateStarterPromptsConfig: StarterPrompt[] = [
    {
        label: 'Announce a new product',
        prompt: 'Announce a new product that we recently launched. Highlight its key features, benefits, and what makes it stand out. Make the tone exciting and encourage customers to learn more or try it.',
    },
    {
        label: 'Invite customers to a webinar',
        prompt: 'Invite customers to our upcoming webinar. Highlight the [topic, date, and time], and explain what attendees will learn. Make it engaging and include a clear call-to-action to register or save their spot.',
    },
    {
        label: 'Promote upcoming sale',
        prompt: 'Promote our upcoming sale. Highlight the dates, special offers, and explain why customers should take advantage of the savings. Make it engaging and include a clear call-to-action to shop now.',
    },
];

const newsletterStarterPromptsConfig: StarterPrompt[] = [
    {
        label: 'Share industry tips',
        prompt: 'Share valuable tips and insights related to our industry. Include 2-3 actionable takeaways our readers can implement right away. Keep the tone helpful and educational to position us as a trusted resource.',
    },
    {
        label: 'Monthly content roundup',
        prompt: 'Create a monthly roundup highlighting our latest blog posts, company updates, and upcoming events. Keep it scannable with brief descriptions and clear calls-to-action to learn more.',
    },
    {
        label: 'Highlight a product or feature',
        prompt: "Spotlight one of our products or features. Explain what it does, who it's for, and how it helps customers. Keep the tone informative and include a call-to-action to learn more or try it out.",
    },
];

const dripSequenceStarterPromptsConfig: StarterPrompt[] = [
    {
        label: 'Welcome new subscribers',
        prompt: 'Welcome a new subscriber to our community. Introduce our brand and what makes us unique, set expectations for future emails, and highlight one valuable resource they should explore first.',
    },
    {
        label: 'Onboard new customers',
        prompt: 'Guide a new customer through getting started with our product. Explain the most important first step they should take and why it matters for their success. Keep it encouraging and action-oriented.',
    },
    {
        label: 'Win back contacts',
        prompt: "Win back a contact who hasn't engaged recently. Acknowledge we miss them, remind them of the value we provide, and offer an enticing reason to reconnect—like exclusive content or a special offer.",
    },
];

function getStarterPromptsConfig(campaignModalType: CampaignModalType): StarterPrompt[] {
    switch (campaignModalType) {
        case 'newsletterEditions':
            return newsletterStarterPromptsConfig;
        case 'dripSequenceEditions':
            return dripSequenceStarterPromptsConfig;
        default:
            return broadcastAndTemplateStarterPromptsConfig;
    }
}

function getEditionTypeLabel(campaignModalType: CampaignModalType) {
    switch (campaignModalType) {
        case 'broadcasts':
            return 'broadcast';
        case 'newsletterEditions':
            return 'newsletter edition';
        case 'dripSequenceEditions':
            return 'drip message';
        case 'campaignTemplates':
            return 'campaign template';
        default:
            return 'edition';
    }
}
